import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { CameraIcon, CloseIcon } from './icons.js';

interface CameraCaptureProps {
  title: string;
  hint: string;
  captureLabel: string;
  busyLabel: string;
  busy: boolean;
  error?: string | null;
  onCapture: (image: Blob) => void;
  onClose: () => void;
}

/**
 * Longest edge of the uploaded image, in pixels.
 *
 * Deliberately generous. Testing the label scanner showed a 400px-wide table
 * produced confidently wrong numbers with rows misaligned, while the same label
 * at ~1600px transcribed exactly. Shrinking further to save bandwidth would
 * trade away the accuracy these features exist for, and on a LAN the bandwidth
 * is not the constraint.
 */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;

/**
 * Camera preview with capture and a gallery fallback.
 *
 * Shared by the nutrition-label scanner and the meal-photo scanner: both need
 * the same rear-camera preview, the same downscale-and-re-encode step, and the
 * same behaviour when the camera is refused. Re-encoding also normalises the
 * format — phones happily produce HEIC, which the vision model cannot decode.
 */
export function CameraCapture({
  title,
  hint,
  captureLabel,
  busyLabel,
  busy,
  error,
  onCapture,
  onClose,
}: CameraCaptureProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function start(): Promise<void> {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // Dense small print and plated food both benefit from resolution.
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1920 } },
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
          setCameraReady(true);
        }
      } catch (caught) {
        if (cancelled) return;

        // Not fatal — the gallery picker still works — but it must be said out
        // loud. A silent black rectangle looks like the app is broken rather
        // than like the camera was refused.
        setCameraReady(false);
        setCameraError(
          caught instanceof DOMException && caught.name === 'NotAllowedError'
            ? 'Kamerazugriff wurde abgelehnt. Erlaube ihn in den Browser-Einstellungen oder wähle ein Bild aus der Galerie.'
            : 'Keine Kamera verfügbar. Wähle stattdessen ein Bild aus der Galerie.',
        );
      }
    }

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) return;

    const blob = await drawToJpeg(video, video.videoWidth, video.videoHeight);
    if (blob) onCapture(blob);
  }, [onCapture]);

  const pickFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const bitmap = await createImageBitmap(file);
      const blob = await drawToJpeg(bitmap, bitmap.width, bitmap.height);
      bitmap.close();

      if (blob) onCapture(blob);
    },
    [onCapture],
  );

  return (
    <div className="stack">
      <div className="row-between">
        <h2>{title}</h2>
        <button className="icon-btn" onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </button>
      </div>

      {cameraError ? (
        <div className="banner banner-warning" role="status">
          {cameraError}
        </div>
      ) : (
        <>
          <div className="camera-frame">
            <video ref={videoRef} playsInline muted />
          </div>
          <p className="hint">{hint}</p>
        </>
      )}

      {error && (
        <div className="banner banner-error" role="alert">
          {error}
        </div>
      )}

      <button
        className="btn btn-primary btn-block"
        disabled={busy || !cameraReady}
        onClick={() => void capture()}
      >
        {busy ? <span className="spinner" /> : <CameraIcon />}
        {busy ? busyLabel : captureLabel}
      </button>

      <label className="btn btn-ghost btn-block" style={{ cursor: 'pointer' }}>
        Aus Galerie wählen
        <input
          type="file"
          accept="image/*"
          className="visually-hidden"
          disabled={busy}
          onChange={(event) => void pickFile(event)}
        />
      </label>
    </div>
  );
}

/**
 * Draws a video frame or bitmap to a canvas, scaled so the longest edge is at
 * most MAX_EDGE, and encodes it as JPEG.
 */
async function drawToJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
): Promise<Blob | null> {
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', JPEG_QUALITY);
  });
}
